import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { collection, getDocs, setDoc } from 'firebase/firestore';
import { EventModal } from './EventModal';
import { CalendarEvent } from './CalendarEvent';

export const Calendar = ({ db, courseId, allAssignments, calendarRef, weeks, setWeeks, currentWeek, setCurrentWeek }) => {

  // State variables for adding an event
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Load the assignments from the CIP firestore
  const [cipAssignments, setCIPAssns] = useState([]);
  useEffect(() => {
    const fetchAssignmentsFromFirestore = async () => {
      const cipAssnsCollection = collection(db, `assns/${courseId}/assignments`)
      const cipAssnsSnapshot = await getDocs(cipAssnsCollection);
      const allAssnIds = cipAssnsSnapshot.docs.map(doc => doc.id);
      setCIPAssns(allAssnIds);
    }

    fetchAssignmentsFromFirestore();
  }, []);

  // Update the current week in the database
  const updateCurrentWeek = async (newWeek) => {
    try {
      // Update the current week in Firestore 
      await setDoc(calendarRef, { currentWeek: newWeek }, { merge: true });

      // Update the local state
      setCurrentWeek(newWeek);
    } catch (error) {
      console.error('Error updating current week:', error);
    }
  };

  // Update the weeks in the database
  const updateWeeks = async (newWeeks) => {
    try {
      // Update the weeks in Firestore
      await setDoc(calendarRef, { weeks: newWeeks }, { merge: true });

      // Update the local state
      setWeeks(newWeeks);
    } catch (error) {
      console.error('Error updating weeks:', error);
    }
  };

  // Go to the previous week
  const goToPrevWeek = async () => {
    if (currentWeek > 0) {
      const prevWeek = currentWeek - 1;
      await updateCurrentWeek(prevWeek);
    }
  };

  // Go to the next week
  const goToNextWeek = async () => {
    if (currentWeek < weeks.length - 1) {
      const nextWeek = currentWeek + 1;
      await updateCurrentWeek(nextWeek);
    }
  };

  // Display a popup to add a new calendar event
  const addCalendarEvent = async (day, week) => {
    setSelectedDay({ day, week });
    setShowAddEventModal(true);
  };

  const handleAddEvent = async (newEvent) => {
    // Create an updated weeks object with the new event
    const updatedWeeks = [...weeks];
    updatedWeeks[selectedDay.week].days = updatedWeeks[selectedDay.week].days.map(d =>
      d.label === selectedDay.day.label
        ? { ...d, events: [...(d.events || []), newEvent] }
        : d,
    );

    // Update the weeks in both the state and database
    await updateWeeks(updatedWeeks);

    // Hide the modal
    setShowAddEventModal(false);
  }

  return (
    <>
      {showAddEventModal && (
        <EventModal
          mode="add"
          initialData={{}} // or sensible defaults
          onSubmit={handleAddEvent}
          onClose={() => setShowAddEventModal(false)}
          allAssignments={allAssignments}
          cipAssignments={cipAssignments}
        />)}
      <CalendarWrapper>
        <CalendarHeader>
          <NavButton
            onClick={() => goToPrevWeek()}
            disabled={currentWeek === 0}
          >
            {'<'}
          </NavButton>
          <DateRange>{weeks[currentWeek].label}</DateRange>
          <NavButton
            onClick={() => goToNextWeek()}
            disabled={currentWeek === weeks.length - 1}
          >
            {'>'}
          </NavButton>
        </CalendarHeader>

        <CalendarGrid>
          {weeks[currentWeek].days.map(day => (
            <DayColumn key={day.label}>
              <DayLabel>
                {day.label}
                <AddEventButton onClick={() => addCalendarEvent(day, currentWeek)}>
                  +
                </AddEventButton>
              </DayLabel>
              {(day.events || []).map((event, idx) => {
                return (
                  <CalendarEvent
                    key={idx}
                    event={event}
                    updateWeeks={updateWeeks}
                    dayLabel={day.label}
                    weekIndex={currentWeek}
                    weeks={weeks}
                    cipAssignments={cipAssignments}
                    allAssignments={allAssignments}
                  />
                );
              })}
            </DayColumn>
          ))}
        </CalendarGrid>
      </CalendarWrapper>
    </>
  );
};

const CalendarWrapper = styled.div`
  border-top: 1px solid #ccc;
  padding-top: 1rem;
  margin-bottom: 2rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const NavButton = styled.button`
  background: #eee;
  border: 1px solid #ccc;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 4px;
`;

const DateRange = styled.h2`
  font-size: 1.1rem;
  margin: 0;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  border: 1px solid #ccc;
  position: relative;
`;

const DayColumn = styled.div`
  border-left: 1px solid #ccc;
  height: 601px;
  position: relative;
  background: white;

  &:first-child {
    border-left: none;
  }
`;

const DayLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 0.9rem;
  padding: 4px;
  background: #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const AddEventButton = styled.button`
  background: darkgray;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: bold;
  padding: 0px 6px;
  z-index: 2;
  &:hover {
    background: gray;
  }
`;
